<template>
  <Teleport to="body">
    <div class="modal-backdrop" @click.self="$emit('close')">
      <div class="modal-container invoice-vendor-code-modal">
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">{{ $t('modals.missingCounterpartyCodes') }}</h2>
              <p class="modal-subtitle">{{ $t('modals.missingCounterpartyCodesSubtitle', counterparties.length, { count: counterparties.length }) }}</p>
            </div>
            <button @click="$emit('close')" class="btn btn-icon">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body">
            <div v-if="loading" class="loading-container">
              <div class="spinner-small"></div>
              <span>{{ $t('modals.saving') }}</span>
            </div>

            <div v-else class="counterparties-list">
              <div v-for="(counterparty, index) in localCounterparties" :key="index" class="counterparty-item">
                <div class="counterparty-info">
                  <div class="counterparty-name-row">
                    <span class="counterparty-name">{{ counterparty.name }}</span>
                    <span
                      v-if="counterparty.vendor_type"
                      class="counterparty-type-badge"
                      :class="counterparty.vendor_type === 'client' ? 'badge-client' : 'badge-supplier'"
                    >
                      {{ counterparty.vendor_type === 'client' ? $t('modals.clientBadge') : $t('modals.supplierBadge') }}
                    </span>
                  </div>
                  <div class="counterparty-vat">{{ counterparty.vat || counterparty.tax_id || $t('modals.noCounterpartyIdentifier') }}</div>
                </div>
                <div class="counterparty-code-input">
                  <input
                    v-model="counterparty.code"
                    type="text"
                    :placeholder="$t('modals.enterCounterpartyCode')"
                    class="form-control"
                    @keypress.enter="saveVendorCodes"
                  />
                </div>
              </div>
            </div>

            <div v-if="error" class="error-message">
              {{ error }}
            </div>
          </div>

          <div class="modal-footer">
            <button @click="$emit('close')" class="btn btn-secondary">
              {{ $t('modals.cancel') }}
            </button>
            <button @click="saveVendorCodes" class="btn btn-primary" :disabled="loading || !allCodesEntered">
              <font-awesome-icon v-if="loading" icon="fa-solid fa-spinner" spin />
              {{ $t('modals.saveAndExport') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';

const { t } = useI18n();

const props = defineProps({
  counterparties: {
    type: Array,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const loading = ref(false);
const error = ref(null);
const localCounterparties = ref(props.counterparties.map(cp => ({ ...cp })));

const allCodesEntered = computed(() => {
  return localCounterparties.value.every(cp => cp.code && cp.code.trim() !== '');
});

const saveVendorCodes = async () => {
  // Check if all counterparties have codes
  const missingCodes = localCounterparties.value.filter(cp => !cp.code || cp.code.trim() === '');
  if (missingCodes.length > 0) {
    error.value = t('modals.addAllCounterpartyCodesError', missingCodes.length, { count: missingCodes.length });
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Prepare codes data
    const codes = localCounterparties.value.map(cp => ({
      vat: cp.vat || '',
      tax_id: cp.tax_id || '',
      code: cp.code.trim(),
      vendor_type: cp.vendor_type || null,
      invoice_ids: cp.invoice_ids
    }));

    // Save vendor codes to invoices
    await api.post('/api/scans/export/save-vendor-codes', { codes });
    
    emit('saved');
  } catch (err) {
    console.error('Failed to save vendor codes:', err);
    
    if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else {
      error.value = t('modals.saveCounterpartyCodesError');
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  position: relative;
  z-index: 9999;
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
}

.invoice-vendor-code-modal {
  max-width: 700px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-title-section {
  flex: 1;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #111827;
}

.modal-subtitle {
  font-size: 14px;
  color: #6c757d;
  margin-top: 4px;
  margin-bottom: 0;
}

.btn-icon {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  transition: color 0.2s;
}

.btn-icon:hover {
  color: #111827;
}

.modal-body {
  padding: 24px;
  flex: 1;
  overflow-y: auto;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: #6b7280;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.counterparties-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.counterparty-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.counterparty-info {
  flex: 1;
  min-width: 0;
}

.counterparty-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  min-width: 0;
}

.counterparty-name {
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.counterparty-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.badge-supplier {
  background: #dbeafe;
  color: #1e40af;
}

.badge-client {
  background: #fef3c7;
  color: #92400e;
}

.counterparty-vat {
  font-size: 13px;
  color: #6b7280;
}

.counterparty-code-input {
  width: 200px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.error-message {
  margin-top: 16px;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 14px;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
